import { useSelector } from '@xstate/react';
import { useEffect, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAlertView, UserContextV2 } from '~/app';
import { Provider } from '~/types';
import { authDebug, summarizeUrl } from './authDebug';
import { resolvePostAuthDestination } from './postAuthRedirect';
import { paths } from './routes';
import { VaultLayout } from './VaultLayout';

const isProfileComplete = (provider: Provider | undefined): boolean => {
  if (!provider) return true; // Don't redirect while provider is still loading
  return (
    provider.firstName != undefined &&
    provider.lastName != undefined &&
    provider.category != undefined &&
    provider.type != undefined
  );
};

/**
 * Protected Vault Route
 *
 * Permissive guard that allows access if:
 * - User is in an authenticated provider state (authenticatedProvider or authenticatedImpersonating)
 *
 * Also redirects to profile page if profile is incomplete (unless already on profile page or the
 * in-app FAQ, which the header ? opens from anywhere, the profile page included).
 * Skips profile check when impersonating since the employer is viewing, not editing.
 *
 * Wraps children in VaultLayout for consistent page structure
 */
export const ProtectedVaultRoute = ({ children }: { children: React.ReactNode }) => {
  const state = UserContextV2.useSelector((s) => s);
  // The user fetch is still settling who this is (first sign-in, or a switch from
  // another account): the session is not signed out, so do not send it to sign in.
  const loading = UserContextV2.useSelector(
    (s) =>
      s.context.loading ||
      s.matches('$_AUTHENTICATING') ||
      s.matches('$_FETCHING_USER') ||
      s.matches('$_DETERMINE_ROLE')
  );
  const location = useLocation();

  const userRef = UserContextV2.useActorRef();
  const vaultRef = useSelector(userRef, (s) => s.context.vaultRef);
  const provider = useSelector(vaultRef, (s) => s?.context.provider);
  const alertRef = useAlertView();

  const isAllowedState =
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_PROVIDER' }) ||
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' });
  const isDelegating = state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_IMPERSONATING' });
  const isEmployer =
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_EMPLOYER' }) ||
    state.matches({ $_AUTHENTICATED: '$_AUTHENTICATED_ADMIN' });
  // [MCV-1724] The FAQ is exempt too: a provider with an incomplete profile who presses the header ?
  // on the profile page would otherwise be sent straight back to it with a "Profile Incomplete" alert.
  const isProfileGateExempt =
    location.pathname === paths.vault.profile || location.pathname === paths.vault.faq;
  const shouldRedirectToProfile =
    !loading &&
    isAllowedState &&
    !isDelegating &&
    !isProfileGateExempt &&
    !isProfileComplete(provider);

  useEffect(() => {
    if (!shouldRedirectToProfile) return;

    alertRef.send({
      type: 'ON_SHOW_ALERT',
      payload: {
        type: 'warning',
        text: 'Profile Incomplete',
        subText: 'Please complete your profile information to continue.',
        timeout: 5000,
      },
    });
  }, [alertRef, shouldRedirectToProfile]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!isAllowedState) {
    if (isEmployer) {
      return (
        <EmployerVaultRedirect
          requestedPath={`${location.pathname}${location.search}${location.hash}`}
        />
      );
    }
    return <Navigate to={paths.publicAuth.signIn} replace />;
  }

  if (isDelegating && location.pathname === paths.vault.settings) {
    return <Navigate to={paths.vault.dashboard} replace />;
  }

  // Redirect to profile if incomplete (skip when impersonating or already on an exempt page).
  if (shouldRedirectToProfile) {
    return <Navigate to={paths.vault.profile} replace />;
  }

  return <VaultLayout> {children} </VaultLayout>;
};

const EmployerVaultRedirect = ({ requestedPath }: { requestedPath: string }) => {
  const destination = useMemo(
    () =>
      resolvePostAuthDestination({
        role: 'employer',
        hostname: window.location.hostname,
        returnTo: requestedPath,
      }),
    [requestedPath]
  );

  useEffect(() => {
    if (destination.type !== 'external') return;

    authDebug('protected-vault.employer_external_redirect', {
      requestedPath,
      target: summarizeUrl(destination.url),
    });
    window.location.assign(destination.url);
  }, [destination, requestedPath]);

  if (destination.type === 'local') {
    authDebug('protected-vault.employer_local_redirect', {
      requestedPath,
      target: destination.path,
    });
    return <Navigate to={destination.path} replace />;
  }

  return null;
};
